// /src/Components/Clients/AdminCampaignView.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Building2, Download } from "lucide-react";
import whatsapp from "../../Assets/whatsapp_background.jpg";

import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";

export default function AdminCampaignView() {
  const { getCampaigns, getWhatsappTemplatesV1 } = useWhatsapp();

  const { clientId, number, CampaignId, campaignName } = useParams();
  const { api } = useContext(GlobalContext);

  const [campaigns, setCampaigns] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCampaigns = async () => {
    setLoading(true);

    try {
      const result =
        await getCampaigns({
          userid: clientId,
          campaignName: decodeURIComponent(
            campaignName || ""
          ),
          phoneNumberId: number,
        });

      if (result.success) {
        setCampaigns(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error("Error loading campaign");
    } finally {
      setLoading(false);
    }
  };


  const loadTemplates = async () => {
    if (!clientId) return;

    try {
      const result =
        await getWhatsappTemplatesV1({ userid: clientId, phoneNumberId: number });

      if (result.success) {
        setTemplateList(result.data);
      }
    } catch (e) {
      // non-blocking (same behavior as before)
    }
  };


  useEffect(() => { loadCampaigns(); loadTemplates(); }, [clientId, number, CampaignId, campaignName]); // eslint-disable-line

  const data = useMemo(() => {
    const camp = campaigns.find((c) => String(c.id) === String(CampaignId));
    const template = camp ? templateList.find((t) => t.name === camp.template_id) : null;
    return { camp, template };
  }, [campaigns, templateList, CampaignId]);

  const fill = (text) => (text || "").replace(/{{(\d+)}}/g, (_, i) => (["John Doe", "Messaging"][i - 1] || ""));

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />
      {/* header */}
      <div className="mb-4 flex items-center gap-2">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
          <Building2 size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Campaign Details</h1>
          <p className="text-sm text-gray-500">{decodeURIComponent(campaignName || "")} — {clientId}</p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* left: info + counters */}
        <div className="rounded-2xl border bg-white p-4">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading…</div>
          ) : !data.camp ? (
            <div className="py-16 text-center text-gray-500">No campaign found.</div>
          ) : (
            <>
              <h3 className="text-base font-semibold mb-2">Campaign Info</h3>
              <div className="overflow-hidden rounded-xl border">
                <table className="min-w-full text-sm">
                  <tbody>
                    <Row name="Campaign Name" value={data.camp.campaign_name} />
                    <Row name="Template Name" value={data.camp.template_id} />
                    <Row name="Start" value={data.camp.campaign_start || "—"} />
                    <Row name="End" value={data.camp.campaign_end || "—"} />
                    <Row name="Channel" value={data.camp.channel || "—"} />
                    <Row
                      name="Audience"
                      value={
                        data.camp.excel_url ? (
                          <a href={data.camp.excel_url} target="_blank" rel="noreferrer" className="text-[#01C37D] inline-flex items-center gap-2 text-sm font-medium hover:underline">
                            < Download size={14} /> Download
                          </a>


                        ) : "N/A"
                      }
                    />
                  </tbody>
                </table>
              </div>

              {/* counters */}
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Stat label="Read" value={data.camp.read} color="text-sky-700 border-sky-200 bg-sky-50" />
                <Stat label="Delivered" value={data.camp.delivered} color="text-emerald-700 border-emerald-200 bg-emerald-50" />
                <Stat label="Accepted" value={data.camp.accepted} color="text-indigo-700 border-indigo-200 bg-indigo-50" />
                <Stat label="Failed" value={data.camp.failed} color="text-rose-700 border-rose-200 bg-rose-50" />
                <Stat label="Responded" value={data.camp.responded} color="text-amber-700 border-amber-200 bg-amber-50" />
                <Stat label="Sent" value={data.camp.sent} color="text-zinc-700 border-zinc-200 bg-zinc-50" />
              </div>
            </>
          )}
        </div>

        {/* right: phone preview */}
        <div className="rounded-2xl border bg-white p-4">
          <h3 className="text-base font-semibold mb-2">Template Preview</h3>
          {!data.template ? (
            <div className="py-16 text-center text-gray-500">No template found.</div>
          ) : (
            <div className="flex justify-center">

              <div className="relative w-[300px] overflow-hidden rounded-[36px] border-[10px] border-black bg-black shadow-2xl">

                {/* notch */}
                <div className="absolute left-1/2 top-0 z-30 h-6 w-40 -translate-x-1/2 rounded-b-3xl bg-black" />

                {/* phone screen */}
                <div className="flex h-[480px] flex-col bg-[#efeae2]">

                  {/* fixed navbar */}
                  <div className="z-20 flex items-center gap-3 bg-[#075e54] px-4 pb-3 pt-8 text-white shadow">

                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#01C37D] font-semibold">
                      C
                    </div>

                    <div className="flex-1">

                      <div className="text-sm font-semibold">
                        Chaafo
                      </div>

                      <div className="text-[11px] text-green-100">
                        online
                      </div>

                    </div>

                  </div>

                  {/* scrollable chats */}
                  <div
                    className="flex-1 overflow-y-auto bg-cover bg-center p-4"
                    style={{
                      backgroundImage: `url(${whatsapp})`,
                    }}
                  >

                    <div className="flex min-h-full flex-col gap-3">

                      {/* date */}
                      <div className="sticky top-0 z-10 flex justify-center">

                        <div className="rounded-xl bg-[#e1f2fb] px-3 py-1 text-[11px] text-gray-600 shadow-sm">
                          Today
                        </div>

                      </div>

                      {/* sent msg */}
                      <div className="ml-auto max-w-[75%] rounded-2xl rounded-tr-md bg-[#d9fdd3] px-4 py-2 shadow-sm">

                        <div className="text-sm text-gray-800">
                          Hello 👋
                        </div>

                        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-gray-500">

                          <span>10:42 AM</span>

                          <svg
                            viewBox="0 0 16 15"
                            height="15"
                            width="16"
                            preserveAspectRatio="xMidYMid meet"
                            className="fill-[#53bdeb]"
                          >
                            <path d="M15.01 3.316l-8.369 8.418-4.53-4.425-1.12 1.122 5.65 5.527 9.49-9.54-1.12-1.102z"></path>
                          </svg>

                        </div>

                      </div>

                      {/* received template */}
                      <div className="max-w-[85%] overflow-hidden rounded-2xl rounded-tl-md bg-white shadow-sm">

                        {/* media */}
                        {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                          data.template.components?.find(
                            (c) => c.format
                          )?.format || ""
                        ) && (

                            <div className="border-b bg-gray-100 p-3">

                              <div className="flex h-44 items-center justify-center rounded-xl bg-gray-200 text-sm font-medium text-gray-500">

                                {
                                  data.template.components?.find(
                                    (c) => c.format
                                  )?.format
                                } Preview

                              </div>

                            </div>
                          )}

                        {/* message */}
                        <div className="space-y-3 px-4 py-3">

                          {/* header */}
                          {data.template.components?.find(
                            (c) => c.type === "HEADER"
                          )?.text && (

                              <div className="text-[15px] font-bold text-gray-900">

                                {
                                  data.template.components.find(
                                    (c) =>
                                      c.type === "HEADER"
                                  )?.text
                                }

                              </div>
                            )}

                          {/* body */}
                          {data.template.components?.find(
                            (c) => c.type === "BODY"
                          )?.text && (

                              <div className="whitespace-pre-wrap text-[14px] leading-6 text-gray-800">

                                {fill(
                                  data.template.components.find(
                                    (c) =>
                                      c.type === "BODY"
                                  )?.text
                                )}

                              </div>
                            )}

                          {/* footer */}
                          {data.template.components?.find(
                            (c) => c.type === "FOOTER"
                          )?.text && (

                              <div className="border-t pt-2 text-[11px] text-gray-500">

                                {
                                  data.template.components.find(
                                    (c) =>
                                      c.type === "FOOTER"
                                  )?.text
                                }

                              </div>
                            )}

                          {/* time */}
                          <div className="flex justify-end text-[10px] text-gray-400">
                            10:43 AM
                          </div>

                        </div>

                        {/* buttons */}
                        {!!(
                          data.template.components?.find(
                            (c) => c.type === "BUTTONS"
                          )?.buttons || []
                        ).length && (

                            <div className="border-t bg-white">

                              {(
                                data.template.components?.find(
                                  (c) =>
                                    c.type === "BUTTONS"
                                )?.buttons || []
                              ).map((b, i) => (

                                <button
                                  key={i}
                                  className="flex w-full items-center justify-center border-b px-4 py-3 text-sm font-medium text-[#00a884] transition hover:bg-gray-50 last:border-b-0"
                                >
                                  {b.text}
                                </button>

                              ))}

                            </div>
                          )}

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ name, value }) {
  return (
    <tr className="border-b last:border-0">
      <th className="w-[40%] px-4 py-2 text-left text-xs font-semibold text-gray-500">{name}</th>
      <td className="px-4 py-2 text-sm">{value ?? "—"}</td>
    </tr>
  );
}
function Stat({ label, value, color }) {
  return (
    <div className={`rounded-xl border px-3 py-2 text-center ${color}`}>
      <div className="text-xs">{label}</div>
      <div className="text-base font-semibold">{value ?? 0}</div>
    </div>
  );
}
